<template>
  <v-container fluid>
    <div v-if="userLoaded && user.can('view_rootentity')">
      <h2>{{ $tc('api_request', 2) }}</h2>
      <v-alert v-if="ignoredQueryParams.length > 0" type="warning" outlined dense class="mt-2">
        {{ $t('ignored_query_parameters', { parameters: ignoredQueryParams.join(', ') }) }}
      </v-alert>
      <api-access-log :init-search-criteria="initSearchCriteria"/>
    </div>
    <v-alert v-else-if="userLoaded" type="info" outlined class="mt-4">
      {{ $t('root_only_page') }}
    </v-alert>
  </v-container>
</template>
<script>

import { isNil, omitBy } from 'lodash'
import ApiAccessLog from '@/components/api-access-log/ApiAccessLog'
import BrowserUtilitiesMixin from '@/components/mixins/BrowserUtilitiesMixin'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'

/**
 * The API request browser page.
 *
 * Root only. The browser is not mounted for anyone else, which also keeps a
 * non-root visitor from firing an index request the API would answer with a 404.
 * The API's own check is the authoritative one: this guard only decides what to
 * render.
 *
 * Accepts the browser's filters as query string parameters so that another view
 * can link into a pre-filtered browser, for example
 * /api-requests?customer_id=42&endpoint=/v2/route
 */
export default {
  name: 'ApiAccessLogIndex',
  components: {
    ApiAccessLog
  },
  mixins: [
    BrowserUtilitiesMixin,
    CurrentUserMixin
  ],
  computed: {
    /**
     * Search criteria taken from the query string. Only keys that are present
     * and usable are returned, so anything else falls back to the browser's own
     * defaults.
     *
     * @return Object
     */
    initSearchCriteria () {
      const query = this.$route.query

      return omitBy({
        userIds: this.idListParam(query.user_id),
        customers: this.idListParam(query.customer_id),
        methods: this.listParam(query.method),
        endpoint: this.csvParam(query.endpoint),
        createdAfter: this.csvParam(query.created_after),
        createdBefore: this.csvParam(query.created_before)
      }, isNil)
    },

    /**
     * The names of query parameters that were supplied but could not be turned
     * into a filter, so the page can say so.
     *
     * Silence is the failure mode that matters here: a link with a malformed
     * filter would otherwise show every request while looking filtered, which
     * is exactly what a drill-down from a statistics view must not do.
     *
     * @return Array
     */
    ignoredQueryParams () {
      const query = this.$route.query
      const applied = this.initSearchCriteria

      return [
        ['user_id', 'userIds'],
        ['customer_id', 'customers'],
        ['method', 'methods'],
        ['endpoint', 'endpoint'],
        ['created_after', 'createdAfter'],
        ['created_before', 'createdBefore']
      ]
        .filter(([queryKey, criteriaKey]) => query[queryKey] !== undefined && applied[criteriaKey] === undefined)
        .map(([queryKey]) => queryKey)
    }
  },
  methods: {
    /**
     * Flattens one query string value into the comma separated form the API
     * accepts, or undefined when there is nothing usable in it.
     *
     * The router parses the query string with qs, so a single parameter can
     * arrive as a string (?p=a and ?p=a,b), as an array (?p[]=a&p[]=b), or as a
     * nested array or object (?p[x][]=a). Only scalars can be filter values.
     * Anything else is dropped rather than passed on, because a nested value
     * reaching String() or trim() downstream throws and takes the page with it.
     *
     * @param {*} value
     * @return {String|undefined}
     */
    csvParam (value) {
      const parts = (Array.isArray(value) ? value : [value])
        .filter(part => !isNil(part) && typeof part !== 'object')
        .map(part => String(part).trim())
        .filter(part => part !== '')

      return parts.length > 0 ? parts.join(',') : undefined
    },

    /**
     * Reads a query string value as a list of strings.
     *
     * @param {*} value
     * @return {Array|undefined}
     */
    listParam (value) {
      const csv = this.csvParam(value)

      return csv === undefined ? undefined : csv.split(',')
    },

    /**
     * Reads a query string value as a list of record ids.
     *
     * Keeps only positive integers, which is what the API accepts, and returns
     * undefined if that leaves nothing. An unusable id is reported by
     * ignoredQueryParams rather than silently widening the search.
     *
     * @param {*} value
     * @return {Array|undefined}
     */
    idListParam (value) {
      const values = this.listParam(value)

      if (values === undefined) {
        return undefined
      }

      const ids = values.map(Number).filter(id => Number.isInteger(id) && id > 0)

      return ids.length > 0 ? ids : undefined
    }
  },
  mounted () {
    this.updateBrowserTitle(this.$tc('api_request', 2))
  }
}
</script>
